import { useMemo, useState } from "react";

import type { ReplayRailNode, ReplaySide } from "@exploitlens/ui-contracts";

import { EmptyState } from "../shell/EmptyState";
import { AlignedCallRow } from "./AlignedCallRow";

const DEFAULT_COLLAPSE_DEPTH = 3;

function RailBranch({
  node,
  byId,
  side,
  depth,
  collapsedIds,
  onToggle,
}: {
  node: ReplayRailNode;
  byId: Map<string, ReplayRailNode>;
  side: ReplaySide;
  depth: number;
  collapsedIds: Set<string>;
  onToggle: (id: string) => void;
}) {
  const children = node.children
    .map((id) => byId.get(id))
    .filter((n): n is ReplayRailNode => n !== undefined);
  const isCollapsed = collapsedIds.has(node.id);

  return (
    <li role="treeitem" aria-expanded={children.length > 0 ? !isCollapsed : undefined}>
      <AlignedCallRow
        node={node}
        side={side}
        depth={depth}
        hasChildren={children.length > 0}
        collapsed={isCollapsed}
        onToggleCollapse={() => onToggle(node.id)}
      />
      {!isCollapsed && children.length > 0 && (
        <ul role="group">
          {children.map((child) => (
            <RailBranch
              key={child.id}
              node={child}
              byId={byId}
              side={side}
              depth={depth + 1}
              collapsedIds={collapsedIds}
              onToggle={onToggle}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

export function ReplayRail({
  side,
  title,
  nodes,
}: {
  side: ReplaySide;
  title: string;
  nodes: ReplayRailNode[];
}) {
  const byId = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);
  const roots = useMemo(() => nodes.filter((n) => n.parentId === null), [nodes]);
  // Collapsed by default past DEFAULT_COLLAPSE_DEPTH so a deep reentrancy
  // loop (real fixture data reaches 24+ levels) doesn't dump a wall of rows
  // on first render; every subtree stays one click away.
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(
    () => new Set(nodes.filter((n) => n.depth === DEFAULT_COLLAPSE_DEPTH).map((n) => n.id)),
  );

  const onToggle = (id: string): void => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <section aria-labelledby={`${side}-rail-heading`} className="min-w-0 flex-1">
      <h2 id={`${side}-rail-heading`} className="font-editorial text-lg text-ink">
        {title}
      </h2>
      {nodes.length === 0 ? (
        <EmptyState title="No call trace is available for this side." />
      ) : (
        <ul role="tree" aria-label={title} className="mt-3 space-y-0.5">
          {roots.map((root) => (
            <RailBranch
              key={root.id}
              node={root}
              byId={byId}
              side={side}
              depth={0}
              collapsedIds={collapsedIds}
              onToggle={onToggle}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
